import React from 'react';
import styles from './LookalikeTable.module.scss';
import type { LookalikeRow } from '../../../store/investor/apiHelpers';
import { filterIconMUDark } from '../../../assets/hostedassets';
import { STATUS_COLORS, COLUMNS } from './constants';
import { useLookalikeTable } from './useLookalikeTable';

interface LookalikeTableProps {
    source: LookalikeRow;
    lookalikes: LookalikeRow[];
    onRowClick: (orgId: string) => void;
}

const SortIcon = () => <img src={filterIconMUDark} alt="" width={14} height={14} className={styles.sortIcon} />;

const CompanyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.companyIcon}>
        <path
            d="M2 14V3h5v2h5v9H2zm1-1h3V4H3v9zm4 0h3V6H7v7zm1-5h1v1H8V8zm0 2h1v1H8v-1zm-4-4h1v1H4V6zm0 2h1v1H4V8zm0 2h1v1H4v-1z"
            fill="#6021b3"
        />
    </svg>
);

const LookalikeTable: React.FC<LookalikeTableProps> = ({ source, lookalikes, onRowClick }) => {
    const { sortKey, sortDir, handleSort, sortedLookalikes, formatInvestors } = useLookalikeTable(lookalikes);

    const renderStatus = (status: string | null) => {
        if (!status) return <span className={styles.naText}>NA</span>;
        const key = status.toLowerCase().trim();
        const style = STATUS_COLORS[key] || { bg: '#f3f4f6', color: '#374151' };
        return (
            <span className={styles.statusPill} style={{ backgroundColor: style.bg, color: style.color }}>
                {status}
            </span>
        );
    };

    const renderCellValue = (row: LookalikeRow, key: string, isSource = false): React.ReactNode => {
        switch (key) {
            case 'similarityScore':
                if (isSource) return <span className={styles.naText}>—</span>;
                return <span className={styles.scoreCell}>{(row.similarityScore * 100).toFixed(0)}%</span>;
            case 'orgName':
                return (
                    <div className={styles.entityCell}>
                        <CompanyIcon />
                        <span className={isSource ? styles.sourceEntityName : styles.entityName} title={row.orgName || ''}>
                            {row.orgName || 'NA'}
                        </span>
                    </div>
                );
            case 'leadStatus':
                return renderStatus(row.leadStatus);
            case 'relationshipStatus':
                return renderStatus(row.relationshipStatus);
            case 'revenue':
                return row.revenue != null ? `₹${Math.round(row.revenue)} Cr` : 'NA';
            case 'ebitdaPct':
                return row.ebitdaPct != null ? `${Math.round(row.ebitdaPct)}%` : 'NA';
            case 'existingDebt':
                return row.existingDebt != null ? `₹${Math.round(row.existingDebt)} Cr` : 'NA';
            case 'equityRaised':
                return row.equityRaised != null ? `₹${Math.round(row.equityRaised)} Cr` : 'NA';
            case 'investors':
                return formatInvestors(row.investors);
            case 'state':
                return <span className={styles.stateCell}>{row.state || 'NA'}</span>;
            default:
                return (row as any)[key] || 'NA';
        }
    };

    return (
        <div className={styles.tableContainer}>
            <table className={styles.lookalikeTable}>
                <thead>
                    <tr>
                        {COLUMNS.map((col) => (
                            <th
                                key={col.key}
                                style={{ width: col.width }}
                                className={col.sortable ? styles.sortableHeader : undefined}
                                aria-sort={
                                    col.sortable && sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                                }
                            >
                                {col.sortable ? (
                                    <button type="button" className={styles.sortButton} onClick={() => handleSort(col.key)}>
                                        <span className={styles.headerContent}>
                                            {col.label}
                                            <SortIcon />
                                        </span>
                                    </button>
                                ) : (
                                    <span className={styles.headerContent}>{col.label}</span>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {/* Source company - fixed at top */}
                    <tr className={styles.sourceRow}>
                        {COLUMNS.map((col) => (
                            <td key={col.key}>{renderCellValue(source, col.key, true)}</td>
                        ))}
                    </tr>

                    {/* Lookalike rows */}
                    {sortedLookalikes.map((row) => (
                        <tr
                            key={row.orgId}
                            className={styles.lookalikeRow}
                            onClick={() => onRowClick(row.orgId)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onRowClick(row.orgId);
                                }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`View ${row.orgName || 'company'} details`}
                        >
                            {COLUMNS.map((col) => (
                                <td key={col.key}>{renderCellValue(row, col.key)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default LookalikeTable;
